import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router";
import { Button, Form, Header, Label, Modal, Segment } from "semantic-ui-react";
import identitySelectors from "../../../../selectors/identity";
import useCreateCrmRequest from "../../../../components/crm/hooks/useCreateCrmRequest";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "react-hook-form";
import { ControlledTextArea } from "../../../../components/ControlledForm";
import config from "../../../../config";

interface TwoWayCommunicationModalProps {
  open: boolean;
  onClose: () => void;
  classificationName: string;
  classificationActive: boolean;
  headline: string;
}
export default function TwoWayCommunicationModal({
  open,
  onClose,
  classificationName,
  classificationActive,
  headline,
}: TwoWayCommunicationModalProps) {
  const { site } = useParams<{ site: string }>();

  const [requestDisplayId, setRequestDisplayId] = useState<string | null>(null);

  const history = useHistory();
  const isLoggedIn = useSelector(identitySelectors.isLoggedIn);

  // If the user is not logged in, make sure we prompt them to login before submitting a request
  useEffect(() => {
    if (!isLoggedIn && open) {
      // When the modal first opens "window.location.search" does not have the query param
      history.push(`/${site}/kickout?redirect=${window.location.pathname}?${`askQuestionModalOpen=${open}`}`);
    }
  }, [isLoggedIn, open]);

  const handleCreateRequest = async ({ displayId }: { displayId: string }) => {
    setRequestDisplayId(displayId);
  };

  const handleClose = () => {
    setRequestDisplayId(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size='tiny'>
      <Segment className='tw-m-0 tw-p-0' basic>
        <TwoWayCommunicationModalBody
          requestDisplayId={requestDisplayId}
          onClose={handleClose}
          classificationName={classificationName}
          classificationActive={classificationActive}
          headline={headline}
          onCreateRequest={handleCreateRequest}
        />
      </Segment>
    </Modal>
  );
}

interface TwoWayCommunicationModalBodyProps {
  requestDisplayId: string | null;
  onClose: () => void;
  onCreateRequest: ({ displayId }: { displayId: string }) => Promise<void>;
  classificationName: string;
  classificationActive: boolean;
  headline: string;
}
function TwoWayCommunicationModalBody({
  requestDisplayId,
  classificationActive,
  classificationName,
  onClose,
  onCreateRequest,
  headline,
}: TwoWayCommunicationModalBodyProps) {
  const { site } = useParams<{ site: string }>();

  if (requestDisplayId) {
    return <TwoWayCommunicationModalConfirmationBody onClose={onClose} requestDisplayId={requestDisplayId} />;
  }

  if (!classificationActive) {
    return (
      <div>
        <Segment basic className='tw-flex tw-justify-center'>
          <p className='tw-text-center'>
            We&apos;re no longer accepting messages for this notification. Additional inquiries can be submitted{" "}
            <a href={`${config.GOGOV_WEB_URL}/${site}/support.php?cmd=shell`}>here</a>
          </p>
        </Segment>
        <Segment basic className='tw-flex tw-bg-[#F5F7F8]'>
          <Button className='tw-grow' primary onClick={onClose}>
            Close
          </Button>
        </Segment>
      </div>
    );
  }

  return (
    <TwoWayCommunicationModalFormBody
      classificationName={classificationName}
      headline={headline}
      onClose={onClose}
      onCreateRequestSuccess={onCreateRequest}
    />
  );
}

function TwoWayCommunicationModalFormBody({
  classificationName,
  headline,
  onCreateRequestSuccess,
  onClose,
}: {
  classificationName: string;
  headline: string;
  onCreateRequestSuccess: ({ displayId }: { displayId: string }) => Promise<void>;
  onClose: () => void;
}) {
  const [requestErrorMessage, setRequestError] = useState<string | null>(null);
  const { createCrmRequest, isLoading } = useCreateCrmRequest();

  const { control, handleSubmit, errors, reset } = useForm({
    defaultValues: {
      question: "",
    },
  });

  const error = errors.question?.message || requestErrorMessage || "";

  const handleClose = () => {
    onClose();
    reset({ question: "" });
  };

  const submitHandler = ({ question }: { question: string }) => {
    const url = new URL(window.location.href);
    url.search = "";
    createCrmRequest(
      {
        classificationName: classificationName,
        description: `${question}\n\n---\nTitle: ${headline}\nSource: ${url.toString()}`,
      },
      {
        onSuccess: (data) => {
          const displayId = data.displayId;
          onCreateRequestSuccess({ displayId });
          reset({ question: "" });
        },
        onError: (err) => {
          const errorMessage = (err as { error?: string })?.error ?? null;
          setRequestError(errorMessage);
        },
      },
    );
  };

  return (
    <Form>
      <Segment basic className='tw-my-0'>
        <Header className='tw-px-2'>
          <Header.Content className='tw-mb-2'>Question or Comment</Header.Content>
          <Header.Subheader>RE: {headline}</Header.Subheader>
        </Header>
      </Segment>
      <Segment basic className='tw-mt-0 tw-py-0'>
        <Form.Field className='tw-px-2'>
          <ControlledTextArea placeholder={headline} control={control} name={"question"} rows={4} />
          {error && (
            <Label basic prompt pointing='above' aria-live='polite' id={`question-error`}>
              {error}
            </Label>
          )}
        </Form.Field>
      </Segment>
      <Segment basic className='tw-flex tw-justify-end tw-bg-[#F5F7F8]'>
        <Button className='tw-mr-4' basic onClick={handleClose}>
          Cancel
        </Button>
        <Button loading={isLoading} primary onClick={handleSubmit(submitHandler)}>
          Send
        </Button>
      </Segment>
    </Form>
  );
}

function TwoWayCommunicationModalConfirmationBody({
  requestDisplayId,
  onClose,
}: {
  requestDisplayId: string;
  onClose: () => void;
}) {
  const { site } = useParams<{ site: string }>();

  return (
    <div>
      <Segment basic>
        <Header className='tw-text-center'>
          <p>
            <FontAwesomeIcon className='tw-text-[#067E30]' icon={faCircleCheck} />
          </p>
          Thank You for Your Submission
        </Header>
      </Segment>
      <Segment basic className='tw-py-0 tw-text-center'>
        <p>
          Your request number is <strong>{requestDisplayId}</strong>
        </p>
        {
          <p>
            You can track the status and send additional inquires on this request{" "}
            <a href={`${config.GOGOV_WEB_URL}/${site}/support.php?cmd=shell`}>here</a>
          </p>
        }
      </Segment>
      <Segment basic className='tw-flex tw-bg-[#F5F7F8]'>
        <Button className='tw-grow' primary onClick={onClose}>
          Done
        </Button>
      </Segment>
    </div>
  );
}
